/**
 * Shared auth refresh utility with retry + backoff.
 *
 * Used as the `onUnauthorized` callback for createClient instances
 * so every app gets consistent refresh behaviour.
 */

/**
 * Auth API base URL.
 *
 * Reads `NEXT_PUBLIC_AUTH_API_URL` first, which `build-stage.ts` injects per
 * stage; the literal is only the last-resort default.
 *
 * That default is PROD, and must stay prod. This constant is compiled into
 * every consuming bundle, so the literal used to be the local-estate URL and
 * every app that reached this module carried it — 22 of them failed
 * `scripts/check-stage-clean.ts` at once, because a grep over built output
 * cannot tell a live origin from the dead right-hand side of an `||`. Keep
 * local hostnames out of this file; point at local with the env var instead.
 *
 * The `typeof process` guard is load-bearing. This module is also built into a
 * standalone IIFE for non-bundled hosts (Gitea), where nothing substitutes
 * `process.env` — a bare reference throws `ReferenceError: process is not
 * defined` at module top level, which kills the whole bundle and every
 * subsequent script on the page. Next builds still collapse this statically,
 * so the stage-injected value is preserved.
 */
export const DEFAULT_AUTH_API =
  (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_AUTH_API_URL) ||
  'https://api.auth.gaz.codes';
const MAX_RETRIES = 3;
const BASE_DELAY = 1000;

export interface CreateAuthRefreshOptions {
  authApiUrl?: string;
}

function resolveAuthApiUrl(override?: string): string {
  return override
    || (typeof process !== 'undefined' && process.env?.NEXT_PUBLIC_AUTH_API_URL)
    || DEFAULT_AUTH_API;
}

/**
 * Refresh the JWT cookie via the auth API. Retries up to 3 times with exponential backoff.
 * Returns true on success, false if the refresh token is invalid or all retries failed.
 */
export async function refreshAuth(options?: CreateAuthRefreshOptions): Promise<boolean> {
  const authApiUrl = resolveAuthApiUrl(options?.authApiUrl);
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(`${authApiUrl}/v1/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) return true;
      if (res.status === 401 || res.status === 403) return false;
    } catch {
      // network error — retry
    }
    if (attempt < MAX_RETRIES) {
      await new Promise(r => setTimeout(r, BASE_DELAY * (2 ** attempt)));
    }
  }
  return false;
}

/**
 * Create an onUnauthorized callback that refreshes the JWT via the auth API.
 * Retries up to 3 times with exponential backoff.
 *
 * **You probably don't want to call this directly.** This is the low-level
 * primitive. For app-owned route fetches, pass it to `createClient` instead,
 * which adds 401-detection, retry-after-refresh, and concurrent-request
 * queueing on top:
 *
 * ```ts
 * import { createAuthRefresh, createClient } from "@workspace/network";
 *
 * export const apiClient = createClient({
 *   baseUrl: "",
 *   credentials: "include",
 *   auth: { onUnauthorized: createAuthRefresh() },
 * });
 * ```
 *
 * Reference apps: agent, terminal, mcp-proxy, sheets, telemetry-dashboard.
 */
export function createAuthRefresh(options?: CreateAuthRefreshOptions) {
  return (): Promise<boolean> => refreshAuth(options);
}

/** Transient HTTP statuses worth retrying — gateway/booting-backend errors. */
const DEFAULT_RETRYABLE_STATUSES = [408, 429, 500, 502, 503, 504];

export interface RetryFetchOptions {
  /** Max retry attempts after the initial try (default: 3). */
  attempts?: number;
  /** Base delay between retries in ms (default: 500). */
  delay?: number;
  /** Cap on any single backoff delay in ms (default: 5000). */
  maxDelay?: number;
  /** HTTP statuses that trigger a retry (default: 408/429/500/502/503/504). */
  retryableStatuses?: number[];
  /** Underlying fetch implementation. Defaults to global fetch. */
  fetch?: typeof fetch;
}

/**
 * Wrap any `typeof fetch` with transient-error retry + exponential backoff
 * (with jitter, honouring `Retry-After`). Retries on the configured 5xx/429/408
 * statuses *and* on thrown network errors (connection refused / reset) — the
 * shapes you see when a backend is still booting after a stack restart and the
 * gateway returns 502/503 or refuses the connection.
 *
 * Compose it *under* `createAuthFetch` so auth refresh wraps the retried call:
 *
 * ```ts
 * const fetchImpl = createAuthFetch({ fetch: withRetryFetch() });
 * ```
 *
 * Mirrors `createClient`'s retry policy for generated `Api` classes (or any
 * consumer expecting a `typeof fetch`) per RFC-002.
 */
export function withRetryFetch(options?: RetryFetchOptions): typeof fetch {
  const attempts = options?.attempts ?? 3;
  const baseDelay = options?.delay ?? 500;
  const maxDelay = options?.maxDelay ?? 5000;
  const retryableStatuses =
    options?.retryableStatuses ?? DEFAULT_RETRYABLE_STATUSES;
  const baseFetch = options?.fetch ?? fetch;

  const computeDelay = (attempt: number, response?: Response): number => {
    const retryAfter = response?.headers.get('retry-after');
    if (retryAfter) {
      const seconds = parseInt(retryAfter, 10);
      if (!isNaN(seconds)) return Math.min(seconds * 1000, maxDelay);
    }
    const exponential = baseDelay * 2 ** attempt;
    const jitter = Math.random() * 0.3 * exponential;
    return Math.min(exponential + jitter, maxDelay);
  };

  return async function retryFetch(input, init) {
    let lastError: unknown;
    for (let attempt = 0; attempt <= attempts; attempt++) {
      // Don't retry a request the caller has already aborted.
      if (init?.signal?.aborted) return baseFetch(input, init);
      try {
        const response = await baseFetch(input, init);
        if (attempt < attempts && retryableStatuses.includes(response.status)) {
          await new Promise((r) => setTimeout(r, computeDelay(attempt, response)));
          continue;
        }
        return response;
      } catch (err) {
        // Network-level failure (connection refused/reset while booting).
        lastError = err;
        if (attempt >= attempts || init?.signal?.aborted) throw err;
        await new Promise((r) => setTimeout(r, computeDelay(attempt)));
      }
    }
    // Exhausted retries on a thrown error.
    throw lastError;
  };
}

export interface CreateAuthFetchOptions extends CreateAuthRefreshOptions {
  /** Paths where 401 is expected (login, refresh, etc.) — skip refresh on those. */
  noRefreshPaths?: string[];
  /** Underlying fetch implementation. Defaults to global fetch. */
  fetch?: typeof fetch;
}

interface PendingRetry {
  resolve: (r: Response) => void;
  reject: (e: unknown) => void;
  retry: () => Promise<Response>;
}

/**
 * Create a `typeof fetch` with 401/403 detection, refresh, dedup, and queueing.
 *
 * Mirrors the auth behaviour of `createClient`'s `requestWithAuth`, but for
 * generated `Api` classes (or any consumer expecting a `typeof fetch`).
 * Concurrent calls during an in-flight refresh share the single refresh and
 * each retry their own request when it completes.
 *
 * Intended as the `customFetch` for `*-client` package runtimes per RFC-002.
 */
export function createAuthFetch(
  options?: CreateAuthFetchOptions,
): typeof fetch {
  const refreshToken = createAuthRefresh(options);
  const noRefreshPaths = options?.noRefreshPaths ?? [];
  const baseFetch = options?.fetch ?? fetch;

  let isRefreshing = false;
  const pending: PendingRetry[] = [];

  return async function authFetch(input, init) {
    const fetchInit: RequestInit = { ...init, credentials: 'include' };
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.href
          : input.url;

    if (noRefreshPaths.some((p) => url.includes(p))) {
      return baseFetch(input, fetchInit);
    }

    const response = await baseFetch(input, fetchInit);
    if (response.status !== 401 && response.status !== 403) {
      return response;
    }
    const originalStatus = response.status;
    const retry = () => baseFetch(input, fetchInit);

    if (isRefreshing) {
      return new Promise<Response>((resolve, reject) => {
        pending.push({ resolve, reject, retry });
      });
    }

    isRefreshing = true;
    try {
      const success = await refreshToken();
      if (success) {
        const queued = pending.splice(0);
        for (const { resolve, reject, retry: queuedRetry } of queued) {
          queuedRetry().then(resolve, reject);
        }
        const retried = await retry();
        // For 403, a still-403 after refresh means the user genuinely lacks
        // permission — surface the response as-is.
        if (originalStatus === 403 && retried.status === 403) return retried;
        return retried;
      }
      // Refresh failed — resolve all queued requests with the original 401/403
      // response so consumers see the auth failure rather than hanging.
      const queued = pending.splice(0);
      for (const { resolve } of queued) resolve(response.clone());
      return response;
    } finally {
      isRefreshing = false;
    }
  };
}
